import React, { useCallback, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import useOrderSheetSetPagePersistedQuery from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import { useToast } from "@chakra-ui/react";
import { findSearchMatchRange } from "@/utils/search-text";

const escapeHtml = (str: string) =>
  str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const highlightText = (text: string, searchTextList: string[]) => {
  const escaped = escapeHtml(text);
  if (!searchTextList.length) return escaped;

  const range = findSearchMatchRange(text, searchTextList);
  if (!range) return escaped;

  const before = escapeHtml(text.slice(0, range.start));
  const match = escapeHtml(text.slice(range.start, range.end));
  const after = escapeHtml(text.slice(range.end));
  return `${before}<span style='background-color: #E2F8FF'>${match}</span>${after}`;
};

const HighlightCellRenderer = ({
  value,
  data,
  colDef,
}: ICellRendererParams) => {
  const { value: query } = useOrderSheetSetPagePersistedQuery();
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const isSkuRow =
    colDef?.field === "brandSKUEtc" &&
    typeof data?.rowId === "string" &&
    data.rowId.endsWith("_1");

  // 음수 id 행은 내부 생성 요약/안내 행(검색 결과 수, "결과가 없습니다" 등)으로
  // brandSKUEtc에 의도된 HTML을 담는다. 이스케이프하지 않고 그대로 렌더한다.
  const isSpecialRow = typeof data?.id === "number" && data.id < 0;

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!value || typeof value !== "string") {
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        // 브라우저는 창에 포커스가 없으면 복사를 거부한다. 화면이 바쁠 때 누르고 바로 다른 창으로
        // 넘어가면 실제 복사 시점에는 포커스가 없다. 알려 주지 않으면 사용자는 복사된 줄 알고
        // 이전 내용을 붙여넣는다(2026-09-30 오더시트에서 실제로 발생).
        toast({
          title: "복사하지 못했습니다",
          description: "다시 눌러 주세요",
          status: "error",
          duration: 2500,
          isClosable: false,
          position: "top",
        });
        return;
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast({
        title: "복사됨",
        description: value,
        status: "success",
        duration: 1500,
        isClosable: false,
        position: "top",
      });
    },
    [value, toast]
  );

  if (!value || typeof value !== "string") {
    return <span>{value}</span>;
  }

  // 요약/안내 행은 의도된 HTML이므로 이스케이프/하이라이트 없이 그대로 렌더
  if (isSpecialRow) {
    return <span dangerouslySetInnerHTML={{ __html: value }} />;
  }

  const highlightedContent = highlightText(value, [
    query?.query ?? "",
    ...(query?.brandName__eq ?? []),
    ...(query?.category__eq ?? []),
    ...(query?.gender__eq ?? []),
    ...(query?.season__in ?? []),
  ]);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        userSelect: isSkuRow ? "none" : undefined,
        cursor: isSkuRow ? "pointer" : undefined,
      }}
      onClick={isSkuRow ? handleCopy : undefined}
    >
      <span dangerouslySetInnerHTML={{ __html: highlightedContent }} />
    </span>
  );
};

export default HighlightCellRenderer;
